import { renumberQuestions } from "../../../utils/surveySchema";

function compressPageNumbers(list = []) {
    const arr = Array.isArray(list) ? list : [];
    if (!arr.length) return arr;
    const pages = Array.from(new Set(arr.map((q) => Number(q.pageNumber || 1)))).sort((a, b) => a - b);
    const map = new Map(pages.map((p, i) => [p, i + 1]));
    return arr.map((q) => ({ ...q, pageNumber: map.get(Number(q.pageNumber || 1)) }));
}

export function createOnUpdateQuestion(setSurvey, currentLanguage, LANGS) {
    return (qid, patch) => {
        setSurvey((prev) => {
            const curLng = currentLanguage;
            const langs = LANGS.filter((l) => prev && Object.prototype.hasOwnProperty.call(prev, l));
            const curQs = prev?.[curLng]?.questionnaire || [];
            const q = curQs.find((x) => x.questionID === qid);
            if (!q) return prev;
            const originalPage = Number(q.pageNumber || 1);
            const idxs = [];
            curQs.forEach((qq, i) => { if (Number(qq.pageNumber) === originalPage) idxs.push(i); });
            const pos = idxs.findIndex((i) => curQs[i].questionID === qid);
            if (pos < 0) return prev;

            const isMetricProvided = Object.prototype.hasOwnProperty.call(patch || {}, 'isMetric');
            const pageNumberProvided = Object.prototype.hasOwnProperty.call(patch || {}, 'pageNumber');
            const prevHasSelect = Array.isArray(q?.details?.selectOptions) && q.details.selectOptions.length > 0;
            const nextHasSelect = Array.isArray(patch?.details?.selectOptions) && patch.details.selectOptions.length > 0;
            const disableDropdown = prevHasSelect && !nextHasSelect;

            const next = { ...prev };
            langs.forEach((lng) => {
                const list = [...(prev?.[lng]?.questionnaire || [])];
                const indices = [];
                list.forEach((qq, i) => { if (Number(qq.pageNumber) === originalPage) indices.push(i); });
                if (pos >= 0 && pos < indices.length) {
                    const idx = indices[pos];
                    if (lng === curLng) {
                        list[idx] = { ...list[idx], ...patch };
                    } else {
                        const p = { ...list[idx] };
                        if (isMetricProvided) p.isMetric = !!patch.isMetric;
                        if (pageNumberProvided) p.pageNumber = Number(patch.pageNumber);
                        if (disableDropdown) {
                            const nextDetails = { ...(p.details || {}) };
                            delete nextDetails.selectOptions;
                            delete nextDetails.dropdownOptions;
                            p.details = nextDetails;
                        }
                        list[idx] = p;
                    }
                }
                next[lng] = { ...(prev?.[lng] || {}), questionnaire: list };
            });

            if (isMetricProvided || pageNumberProvided) {
                langs.forEach((lng) => {
                    const list = next?.[lng]?.questionnaire || [];
                    next[lng] = { ...(next?.[lng] || {}), questionnaire: renumberQuestions(list) };
                });
            }
            return next;
        });
    };
}

export function createOnAddPage(setSurvey, LANGS, defaultLanguage) {
    return () => {
        setSurvey((prev) => {
            let langs = LANGS.filter((l) => prev && Object.prototype.hasOwnProperty.call(prev, l));
            const next = { ...prev };
            if (!langs.length) {
                const def = defaultLanguage || LANGS[0];
                if (!def) return prev;
                next[def] = { questionnaire: [] };
                langs = [def];
            }
            let maxPage = 0;
            langs.forEach((lng) => {
                const qs = next?.[lng]?.questionnaire || [];
                qs.forEach((q) => { maxPage = Math.max(maxPage, Number(q.pageNumber || 1)); });
            });
            const newPage = maxPage + 1;
            const mkQ = () => ({
                questionID: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
                pageNumber: newPage,
                questionType: 'SC',
                questionContent: '',
                details: { options: ['', ''] },
            });
            langs.forEach((lng) => {
                const section = next?.[lng] || {};
                const qs = Array.isArray(section.questionnaire) ? section.questionnaire : [];
                next[lng] = {
                    ...section,
                    questionnaire: renumberQuestions([...qs, mkQ()]),
                };
            });
            return next;
        });
    };
}

export function createOnAddQuestion(setSurvey, LANGS, defaultLanguage) {
    return (pageNumber) => {
        setSurvey((prev) => {
            let langs = LANGS.filter((l) => prev && Object.prototype.hasOwnProperty.call(prev, l));
            const next = { ...prev };
            if (!langs.length) {
                const def = defaultLanguage || LANGS[0];
                if (!def) return prev;
                next[def] = { questionnaire: [] };
                langs = [def];
            }
            const mkQ = () => ({
                questionID: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
                pageNumber,
                questionType: 'SC',
                questionContent: '',
                details: { options: ['', ''] },
            });
            langs.forEach((lng) => {
                const section = next?.[lng] || {};
                const qs = Array.isArray(section.questionnaire) ? section.questionnaire : [];
                next[lng] = {
                    ...section,
                    questionnaire: renumberQuestions([...qs, mkQ()]),
                };
            });
            return next;
        });
    };
}

export function createOnRemovePage(setSurvey, LANGS) {
    return (pageNumber) => {
        setSurvey((prev) => {
            const langs = LANGS.filter((l) => prev && Object.prototype.hasOwnProperty.call(prev, l));
            if (!langs.length) return prev;
            const next = { ...prev };
            langs.forEach((lng) => {
                const removed = (prev?.[lng]?.questionnaire || []).filter((q) => Number(q.pageNumber) !== Number(pageNumber));
                const compressed = compressPageNumbers(removed);
                next[lng] = { ...(prev?.[lng] || {}), questionnaire: renumberQuestions(compressed) };
            });
            return next;
        });
    };
}

export function createOnInsertPageAt(setSurvey, LANGS, defaultLanguage) {
    // Inserts a new page at 1-based position 'atPos'; shifts existing pages >= atPos by +1; adds a default question on the new page
    return (atPos) => {
        setSurvey((prev) => {
            let langs = LANGS.filter((l) => prev && Object.prototype.hasOwnProperty.call(prev, l));
            const next = { ...prev };
            if (!langs.length) {
                const def = defaultLanguage || LANGS[0];
                if (!def) return prev;
                next[def] = { questionnaire: [] };
                langs = [def];
            }
            const pos = Math.max(1, Number(atPos || 1));
            const mkQ = (pageNumber) => ({
                questionID: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
                pageNumber,
                questionType: 'SC',
                questionContent: '',
                details: { options: ['', ''] },
            });
            langs.forEach((lng) => {
                const list = [...(next?.[lng]?.questionnaire || [])];
                // Shift pages >= pos
                const shifted = list.map((q) => {
                    const pn = Number(q.pageNumber || 1);
                    return pn >= pos ? { ...q, pageNumber: pn + 1 } : q;
                });
                // Add a default question on the inserted page
                shifted.push(mkQ(pos));
                const compressed = compressPageNumbers(shifted);
                next[lng] = { ...(next?.[lng] || {}), questionnaire: renumberQuestions(compressed) };
            });
            return next;
        });
    };
}

export function createOnMovePageToPosition(setSurvey, LANGS) {
    // Moves an entire page (by current pageNumber) to target 1-based position; reorders all pages and renumbers
    return (sourcePageNumber, targetPos) => {
        setSurvey((prev) => {
            const langs = LANGS.filter((l) => prev && Object.prototype.hasOwnProperty.call(prev, l));
            if (!langs.length) return prev;
            const src = Number(sourcePageNumber);
            const tgt = Math.max(1, Number(targetPos));
            const next = { ...prev };
            langs.forEach((lng) => {
                const list = [...(prev?.[lng]?.questionnaire || [])];
                if (!list.length) { next[lng] = { ...(prev?.[lng] || {}), questionnaire: [] }; return; }
                // Build grouped pages in order
                const pagesMap = new Map();
                list.forEach((q) => {
                    const pn = Number(q.pageNumber || 1);
                    if (!pagesMap.has(pn)) pagesMap.set(pn, []);
                    pagesMap.get(pn).push(q);
                });
                const pages = Array.from(pagesMap.entries()).sort((a, b) => a[0] - b[0]).map(([, arr]) => arr);
                const srcIdx = Math.max(0, pages.findIndex((arr) => Number(arr?.[0]?.pageNumber || 1) === src));
                if (srcIdx < 0) { next[lng] = { ...(prev?.[lng] || {}), questionnaire: renumberQuestions(list) }; return; }
                const tgtIdx = Math.min(Math.max(0, tgt - 1), pages.length - 1);
                const moving = pages.splice(srcIdx, 1)[0];
                pages.splice(tgtIdx, 0, moving);
                // Flatten and reassign page numbers sequentially
                const reassigned = [];
                pages.forEach((group, index) => {
                    const newPage = index + 1;
                    group.forEach((q) => reassigned.push({ ...q, pageNumber: newPage }));
                });
                const compressed = compressPageNumbers(reassigned);
                next[lng] = { ...(prev?.[lng] || {}), questionnaire: renumberQuestions(compressed) };
            });
            return next;
        });
    };
}

export function createOnRemoveQuestion(setSurvey, currentLanguage, LANGS) {
    return (qid) => {
        setSurvey((prev) => {
            const curLng = currentLanguage;
            const langs = LANGS.filter((l) => prev && Object.prototype.hasOwnProperty.call(prev, l));
            const curQs = prev?.[curLng]?.questionnaire || [];
            const q = curQs.find((x) => x.questionID === qid);
            if (!q) return prev;
            const page = Number(q.pageNumber || 1);
            const idxs = [];
            curQs.forEach((qq, i) => { if (Number(qq.pageNumber) === page) idxs.push(i); });
            const pos = idxs.findIndex((i) => curQs[i].questionID === qid);
            if (pos < 0) return prev;
            const next = { ...prev };
            langs.forEach((lng) => {
                const list = [...(prev?.[lng]?.questionnaire || [])];
                const indices = [];
                list.forEach((qq, i) => { if (Number(qq.pageNumber) === page) indices.push(i); });
                if (pos >= 0 && pos < indices.length) {
                    list.splice(indices[pos], 1);
                }
                const compressed = compressPageNumbers(list);
                next[lng] = { ...(prev?.[lng] || {}), questionnaire: renumberQuestions(compressed) };
            });
            return next;
        });
    };
}

export function createOnMoveWithinPage(setSurvey, currentLanguage, LANGS) {
    return (pageNumber, qid, dir) => {
        setSurvey((prev) => {
            const curLng = currentLanguage;
            const langs = LANGS.filter((l) => prev && Object.prototype.hasOwnProperty.call(prev, l));
            const curQs = prev?.[curLng]?.questionnaire || [];
            const page = Number(pageNumber);
            const idxs = [];
            curQs.forEach((qq, i) => { if (Number(qq.pageNumber) === page) idxs.push(i); });
            const pos = idxs.findIndex((i) => curQs[i].questionID === qid);
            if (pos < 0) return prev;
            const targetPos = pos + dir;
            if (targetPos < 0 || targetPos >= idxs.length) return prev;
            const next = { ...prev };
            langs.forEach((lng) => {
                const list = [...(prev?.[lng]?.questionnaire || [])];
                const indices = [];
                list.forEach((qq, i) => { if (Number(qq.pageNumber) === page) indices.push(i); });
                if (targetPos >= 0 && targetPos < indices.length) {
                    const a = indices[pos];
                    const b = indices[targetPos];
                    const tmp = list[a];
                    list[a] = list[b];
                    list[b] = tmp;
                }
                next[lng] = { ...(prev?.[lng] || {}), questionnaire: renumberQuestions(list) };
            });
            return next;
        });
    };
}
